// app_client/src/components/invoices/InvoiceItemsTable.jsx
import React from 'react';
import { Plus, Trash2, Package, Search } from 'lucide-react';
import useInvoiceStore from '../../store/useInvoiceStore';
import useInvoiceCalculator from '../../hooks/useInvoiceCalculator';

const currencySymbols = {
  TRY: '₺',
  USD: '$',
  EUR: '€',
  GBP: '£'
};

/**
 * InvoiceItemsTable Bileşeni - Store & Hook Bağlantılı
 */
const InvoiceItemsTable = ({ onOpenProductModal, binding = null }) => {
  const store = useInvoiceStore();
  const { activeInvoice, setActiveInvoiceItems } = binding || store;
  // Hook'tan ihtiyacımız olan fonksiyonları çekiyoruz
  const { handleItemChange, calculateAllTotals } = useInvoiceCalculator(binding);

  const items = activeInvoice?.items || [];

  const removeItem = (index) => {
    const newItems = items.filter((_, i) => i !== index);
    setActiveInvoiceItems(newItems);
    // Hook içindeki merkezi hesaplamayı tetikliyoruz
    calculateAllTotals(newItems); 
  };

  return (
    <div className="space-y-4">
      {/* Üst Başlık ve Satır Ekleme Butonu */}
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-[10px] font-black uppercase tracking-widest text-slate-900 ml-4">
          Fatura Kalemleri
        </h3>
        <button 
          type="button"
          onClick={() => onOpenProductModal()}
          className="flex items-center gap-2 text-xs font-black text-blue-600 hover:bg-blue-50 px-4 py-2 rounded-xl transition-all"
        >
          <Plus size={16} /> Satır Ekle
        </button>
      </div>

      <div className="overflow-hidden"> 
        <table className="w-full border-separate border-spacing-y-2 table-fixed">
          <thead>
            <tr className="text-[10px] font-black text-slate-900 uppercase tracking-tighter">
              <th className="pb-2 px-4 text-left w-auto">Ürün / Hizmet</th>
              <th className="pb-2 px-2 text-center w-20">Miktar</th> 
              <th className="pb-2 px-2 text-center w-40">Birim Fiyat</th>
              <th className="pb-2 px-2 text-center w-28">KDV</th>
              <th className="pb-2 px-2 text-center w-36">İskonto</th>
              <th className="pb-2 px-4 text-right w-40">Toplam</th>
              <th className="pb-2 w-12"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {items.map((item, index) => (
              <tr key={index} className="group">
                {/* 1. Ürün Seçimi */}
                <td className="py-4 px-4 align-top"> 
                  <div className="flex flex-col gap-1.5">
                    <button
                      type="button"
                      onClick={() => onOpenProductModal(index)}
                      className="flex items-center justify-between w-full px-3 py-2 bg-slate-50 border border-transparent hover:border-blue-200 rounded-xl transition-all group/btn min-h-10"
                    >
                      <span className="font-bold text-[11px] text-slate-900 truncate pr-2">
                        {item.name || "Seç..."}
                      </span>
                      <Search size={10} className="text-slate-300 shrink-0" />
                    </button>
                    <input 
                      type="text"
                      placeholder="Açıklama ekle..."
                      className="bg-transparent border-none text-[9px] font-medium text-slate-900 placeholder:text-slate-600 focus:ring-0 outline-none w-full px-1"
                      value={item.description || ''}
                      onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                    />
                  </div>
                </td>

                {/* 2. Miktar */}
                <td className="py-4 px-2 align-top">
                  <input 
                    type="number"
                    className="w-full bg-slate-50 border-none rounded-lg py-2 text-center font-bold text-slate-900 outline-none h-10 px-1 text-sm"
                    value={item.quantity}
                    onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                  />
                </td>

                {/* 3. Birim Fiyat */}
                <td className="py-4 px-2 align-top">
                  <div className="relative">
                    <input 
                      type="number"
                      step="0.01"
                      placeholder="0,00"
                      className="w-full bg-slate-50 border-none rounded-lg py-2 text-right pr-3 font-bold text-slate-900 outline-none h-10 text-sm"
                      value={item.unitPrice}
                      onChange={(e) => handleItemChange(index, 'unitPrice', e.target.value)}
                    />
                  </div>
                </td>

                {/* 4. KDV Ayarları */}
                <td className="py-4 px-2 align-top">
                  <div className="flex items-center bg-slate-50 rounded-lg h-10 px-2 gap-1">
                    <input 
                      type="number"
                      className="w-8 bg-transparent border-none text-center font-bold text-slate-900 outline-none text-[11px]"
                      value={item.vatRate}
                      onChange={(e) => handleItemChange(index, 'vatRate', e.target.value)}
                    />
                    <select
                      className="flex-1 bg-transparent border-none text-[9px] font-black uppercase outline-none cursor-pointer appearance-none text-slate-900"
                      value={item.isVatIncluded ? 'true' : 'false'}
                      onChange={(e) => handleItemChange(index, 'isVatIncluded', e.target.value === 'true')}
                    >
                      <option value="false">Hariç</option>
                      <option value="true">Dahil</option>
                    </select>
                  </div>
                </td>

                {/* 5. İskonto Ayarları */}
                <td className="py-4 px-2 align-top">
                  <div className="flex items-center bg-slate-50 rounded-lg h-10 px-2 gap-1">
                    <input 
                      type="number"
                      className="w-full bg-transparent border-none text-center font-bold text-slate-900 outline-none text-[11px]"
                      placeholder="0.00"
                      value={item.discountValue || ''}
                      onChange={(e) => handleItemChange(index, 'discountValue', e.target.value)}
                    />
                    <select
                      className="w-6 bg-transparent border-none text-[10px] font-black outline-none text-slate-900 cursor-pointer appearance-none"
                      value={item.discountType || 'percentage'}
                      onChange={(e) => handleItemChange(index, 'discountType', e.target.value)}
                    >
                      <option value="percentage">%</option>
                      <option value="amount">₺</option>
                    </select>
                  </div>
                </td>

                {/* 6. Toplam Tutarlar */}
                <td className="py-4 px-4 text-right align-top">
                  <div className="flex flex-col justify-center min-h-10">
                    <div className="font-black text-slate-900 text-[13px] leading-tight">
                      {new Intl.NumberFormat('tr-TR', { 
                        minimumFractionDigits: 2, 
                        maximumFractionDigits: 2 
                      }).format(item.grandTotal || 0)}
                    </div>
                    {item.discountAmount > 0 && (
                      <div className="text-[10px] font-bold text-rose-500 whitespace-nowrap leading-tight mt-0.5">
                        -{new Intl.NumberFormat('tr-TR', { 
                          minimumFractionDigits: 2, 
                          maximumFractionDigits: 2 
                        }).format(item.discountAmount)} {currencySymbols[activeInvoice?.currency] || activeInvoice?.currency}
                      </div>
                    )}
                  </div>
                </td>

                {/* 7. Aksiyonlar */}
                <td className="py-4 pr-2 text-center align-top">
                  <div className="flex items-center justify-center h-10">
                    <button 
                      type="button"
                      onClick={() => removeItem(index)} 
                      className="p-2 text-slate-300 hover:text-rose-500 hover:bg-rose-50 rounded-lg transition-all"
                    >
                      <Trash2 size={16}/>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Boş Durum */}
      {items.length === 0 && (
        <div 
          onClick={() => onOpenProductModal()}
          className="border-2 border-dashed border-slate-100 rounded-4xl py-16 flex flex-col items-center justify-center gap-3 cursor-pointer hover:bg-slate-50 transition-all group"
        >
          <Package className="text-slate-200 group-hover:text-blue-400" size={40} />
          <p className="text-xs font-black text-slate-400 uppercase tracking-widest text-center px-4">
            Henüz kalem eklenmedi.<br/>
            <span className="text-blue-500 text-[10px]">Ürün veya Hizmet Seçmek için Tıklayın</span>
          </p>
        </div>
      )}
    </div>
  );
};

export default InvoiceItemsTable;
